import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { useAuth } from "../context/AuthContext.jsx";

/**
 * useDashboardData(page)
 *
 * Fetches /api/dashboard/{page} and returns { data, loading, error, refetch }.
 * Auto-reads ?from= and ?to= from the URL (via DateRangePicker) — when the
 * user changes the range, every dashboard page refetches transparently.
 *
 * On 401 (expired token), calls logout() so ProtectedRoute bounces to /login.
 */
export function useDashboardData(page) {
  const { logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: null,
  });
  const [nonce, setNonce] = useState(0);

  // Forward date range + admin view-as params to the API. Anything else in
  // the URL is ignored — we don't want random search params leaking into
  // the API surface.
  const query = new URLSearchParams();
  const from     = searchParams.get("from");
  const to       = searchParams.get("to");
  const clientId = searchParams.get("clientId");
  if (from)     query.set("from", from);
  if (to)       query.set("to", to);
  if (clientId) query.set("clientId", clientId);
  const queryString = query.toString();
  const url = `/api/dashboard/${page}${queryString ? `?${queryString}` : ""}`;

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    apiFetch(url)
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 401) {
          logout();
          return;
        }
        setState({ data: null, loading: false, error: err });
      });

    return () => {
      cancelled = true;
    };
  }, [url, logout, nonce]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return { ...state, refetch };
}
